'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function InterestCalculator() {
  const t = useTranslations("tools.interestCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    initialInvestment: 20000,
    annualContribution: 5000,
    monthlyContribution: 0,
    contributionTiming: 'beginning',
    interestRate: 5,
    compoundFrequency: 'annually',
    years: 5,
    months: 0,
    taxRate: 0,
    inflationRate: 3,
  });

  const [results, setResults] = useState<any>(null);
  const [schedule, setSchedule] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    handleCalculate();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const compoundFrequencies: { [key: string]: number } = {
    'annually': 1,
    'semiannually': 2,
    'quarterly': 4,
    'monthly': 12,
    'semimonthly': 24,
    'biweekly': 26,
    'weekly': 52,
    'daily': 365
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateCompoundInterest = (
    principal: number,
    rate: number,
    time: number,
    n: number,
    pmt: number,
    pmtTiming: string
  ) => {
    const r = rate / 100;
    const t = time;

    let futureValue;

    if (pmt === 0) {
      futureValue = principal * Math.pow(1 + r / n, n * t);
    } else {
      const compoundFactor = Math.pow(1 + r / n, n * t);
      futureValue = principal * compoundFactor;

      if (pmtTiming === 'beginning') {
        futureValue += pmt * (compoundFactor - 1) / (r / n) * (1 + r / n);
      } else {
        futureValue += pmt * (compoundFactor - 1) / (r / n);
      }
    }

    return futureValue;
  };

  const handleCalculate = () => {
    const totalYears = inputs.years + inputs.months / 12;
    const n = compoundFrequencies[inputs.compoundFrequency];

    const periodicContribution = inputs.annualContribution / n + (inputs.monthlyContribution * 12) / n;

    const endingBalance = calculateCompoundInterest(
      inputs.initialInvestment,
      inputs.interestRate,
      totalYears,
      n,
      periodicContribution,
      inputs.contributionTiming
    );

    const totalContributions = (inputs.annualContribution * totalYears) + (inputs.monthlyContribution * 12 * totalYears);
    const totalPrincipal = inputs.initialInvestment + totalContributions;
    const totalInterest = endingBalance - totalPrincipal;

    const endingBalanceInitial = calculateCompoundInterest(
      inputs.initialInvestment,
      inputs.interestRate,
      totalYears,
      n,
      0,
      inputs.contributionTiming
    );
    const interestInitial = endingBalanceInitial - inputs.initialInvestment;
    const interestContributions = totalInterest - interestInitial;

    const afterTaxInterest = totalInterest * (1 - inputs.taxRate / 100);
    const afterTaxBalance = totalPrincipal + afterTaxInterest;

    const inflationAdjusted = endingBalance / Math.pow(1 + inputs.inflationRate / 100, totalYears);

    setResults({
      endingBalance,
      totalPrincipal,
      totalContributions,
      totalInterest,
      interestInitial,
      interestContributions,
      afterTaxBalance,
      inflationAdjusted,
    });

    // Generate schedule
    const scheduleData = [];
    let currentBalance = inputs.initialInvestment;

    for (let year = 1; year <= Math.ceil(totalYears); year++) {
      const yearTime = Math.min(year, totalYears) - (year - 1);
      const yearContribution = (inputs.annualContribution + inputs.monthlyContribution * 12) * yearTime;

      const newBalance = calculateCompoundInterest(
        inputs.initialInvestment,
        inputs.interestRate,
        Math.min(year, totalYears),
        n,
        periodicContribution,
        inputs.contributionTiming
      );

      const yearInterest = newBalance - currentBalance - yearContribution;
      const yearPrincipal = inputs.initialInvestment + (inputs.annualContribution + inputs.monthlyContribution * 12) * Math.min(year, totalYears);

      scheduleData.push({
        year,
        principal: yearPrincipal,
        interest: yearInterest,
        balance: newBalance,
      });

      currentBalance = newBalance;

      if (year >= totalYears) break;
    }

    setSchedule(scheduleData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCalculate();
  };

  const drawPieChart = () => {
    if (!results) return null;

    const { totalPrincipal, totalInterest } = results;
    const total = inputs.initialInvestment + results.totalContributions + totalInterest;

    if (total === 0) return null;

    const initialPercent = (inputs.initialInvestment / total) * 100;
    const contributionsPercent = (results.totalContributions / total) * 100;
    const interestPercent = (totalInterest / total) * 100;

    const centerX = 140;
    const centerY = 100;
    const radius = 70;

    let currentAngle = -90;

    const createSlice = (percent: number, color: string) => {
      const angle = (percent / 100) * 360;
      const startAngle = currentAngle * Math.PI / 180;
      const endAngle = (currentAngle + angle) * Math.PI / 180;

      const x1 = centerX + radius * Math.cos(startAngle);
      const y1 = centerY + radius * Math.sin(startAngle);
      const x2 = centerX + radius * Math.cos(endAngle);
      const y2 = centerY + radius * Math.sin(endAngle);

      const largeArc = angle > 180 ? 1 : 0;

      const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

      const labelAngle = (currentAngle + angle / 2) * Math.PI / 180;
      const labelX = centerX + (radius * 0.7) * Math.cos(labelAngle);
      const labelY = centerY + (radius * 0.7) * Math.sin(labelAngle);

      const slice = (
        <g key={currentAngle}>
          <path d={path} fill={color} />
          <text
            x={labelX}
            y={labelY}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fontWeight="bold"
            fontSize="14"
          >
            {Math.round(percent)}%
          </text>
        </g>
      );

      currentAngle += angle;
      return slice;
    };

    return (
      <svg width="280" height="200" style={{ display: 'block', margin: '0 auto' }}>
        {createSlice(initialPercent, '#2563eb')}
        {createSlice(contributionsPercent, '#8b5cf6')}
        {createSlice(interestPercent, '#f59e0b')}
      </svg>
    );
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '100%' },
    { maxWidth: '100%' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = { ...inputStyle };

  const inputRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const radioGroupStyle = {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
    flexWrap: 'wrap' as const,
  };

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 500,
    cursor: 'pointer',
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem',
    borderBottom: '1px solid #e2e8f0',
  };

  const resultLabelStyle = {
    color: '#64748b',
    fontWeight: 500,
  };

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const resultHighlightStyle = {
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    borderRadius: '0.5rem',
    padding: '1.25rem',
    marginBottom: '1rem',
  };

  const highlightValueStyle = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const chartContainerStyle = {
    marginTop: '2rem',
    textAlign: 'center' as const,
  };

  const chartLegendStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    marginTop: '1rem',
    flexWrap: 'wrap' as const,
  };

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const legendColorStyle = {
    width: '20px',
    height: '14px',
    borderRadius: '3px',
  };

  const scheduleTableStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '2rem',
      overflowX: 'auto' as const,
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
  };

  const thStyle = {
    padding: '0.75rem',
    textAlign: 'right' as const,
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    fontWeight: 600,
    color: '#334155',
  };

  const thFirstStyle = {
    ...thStyle,
    textAlign: 'left' as const,
  };

  const tdStyle = {
    padding: '0.75rem',
    textAlign: 'right' as const,
    borderBottom: '1px solid #e2e8f0',
  };

  const tdFirstStyle = {
    ...tdStyle,
    textAlign: 'left' as const,
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={{ textAlign: 'center' as const, marginBottom: '2rem' }}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              <form onSubmit={handleSubmit}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="initial-investment">{t("initialInvestmentLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="initial-investment"
                    value={inputs.initialInvestment}
                    onChange={(e) => setInputs({...inputs, initialInvestment: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="annual-contribution">{t("annualContributionLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="annual-contribution"
                    value={inputs.annualContribution}
                    onChange={(e) => setInputs({...inputs, annualContribution: parseFloat(e.target.value) || 0})}
                    step="0.01"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="monthly-contribution">{t("monthlyContributionLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="monthly-contribution"
                    value={inputs.monthlyContribution}
                    onChange={(e) => setInputs({...inputs, monthlyContribution: parseFloat(e.target.value) || 0})}
                    step="0.01"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("contributeAtLabel")}</label>
                  <div style={radioGroupStyle}>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="contribution-timing"
                        value="beginning"
                        checked={inputs.contributionTiming === 'beginning'}
                        onChange={(e) => setInputs({...inputs, contributionTiming: e.target.value})}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      {t("beginningOption")}
                    </label>
                    <label style={radioLabelStyle}>
                      <input
                        type="radio"
                        name="contribution-timing"
                        value="end"
                        checked={inputs.contributionTiming === 'end'}
                        onChange={(e) => setInputs({...inputs, contributionTiming: e.target.value})}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      {t("endOption")}
                    </label>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-rate">{t("interestRateLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="interest-rate"
                    value={inputs.interestRate}
                    onChange={(e) => setInputs({...inputs, interestRate: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="compound-frequency">{t("compoundFrequencyLabel")}</label>
                  <select
                    style={selectStyle}
                    id="compound-frequency"
                    value={inputs.compoundFrequency}
                    onChange={(e) => setInputs({...inputs, compoundFrequency: e.target.value})}
                  >
                    <option value="annually">{t("annuallyOption")}</option>
                    <option value="semiannually">{t("semiannuallyOption")}</option>
                    <option value="quarterly">{t("quarterlyOption")}</option>
                    <option value="monthly">{t("monthlyOption")}</option>
                    <option value="semimonthly">{t("semimonthlyOption")}</option>
                    <option value="biweekly">{t("biweeklyOption")}</option>
                    <option value="weekly">{t("weeklyOption")}</option>
                    <option value="daily">{t("dailyOption")}</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t("investmentLengthLabel")}</label>
                  <div style={inputRowStyle}>
                    <div>
                      <input
                        style={inputStyle}
                        type="number"
                        id="years"
                        value={inputs.years}
                        onChange={(e) => setInputs({...inputs, years: parseInt(e.target.value) || 0})}
                        min="0"
                        placeholder={t("yearsPlaceholder")}
                      />
                    </div>
                    <div>
                      <input
                        style={inputStyle}
                        type="number"
                        id="months"
                        value={inputs.months}
                        onChange={(e) => setInputs({...inputs, months: parseInt(e.target.value) || 0})}
                        min="0"
                        max="11"
                        placeholder={t("monthsPlaceholder")}
                      />
                    </div>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="tax-rate">{t("taxRateLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="tax-rate"
                    value={inputs.taxRate}
                    onChange={(e) => setInputs({...inputs, taxRate: parseFloat(e.target.value) || 0})}
                    step="0.01"
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="inflation-rate">{t("inflationRateLabel")}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="inflation-rate"
                    value={inputs.inflationRate}
                    onChange={(e) => setInputs({...inputs, inflationRate: parseFloat(e.target.value) || 0})}
                    step="0.01"
                  />
                </div>

                <button type="submit" style={calculateBtnStyle}>
                  {t("calculateButton")}
                </button>
              </form>
            </div>

            {results && (
              <div style={resultsStyle}>
                <h3 style={h3Style}>{t("resultsTitle")}</h3>

                <div style={resultHighlightStyle}>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t("endingBalanceLabel")}</span>
                    <span style={highlightValueStyle}>{formatCurrency(results.endingBalance)}</span>
                  </div>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalPrincipalLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalPrincipal)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalContributionsLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalContributions)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalInterestLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.totalInterest)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("interestInitialLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.interestInitial)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("interestContributionsLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.interestContributions)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("afterTaxLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.afterTaxBalance)}</span>
                </div>

                <div style={{...resultItemStyle, borderBottom: 'none'}}>
                  <span style={resultLabelStyle}>{t("inflationAdjustedLabel")}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.inflationAdjusted)}</span>
                </div>

                <div style={chartContainerStyle}>
                  {drawPieChart()}
                  <div style={chartLegendStyle}>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: '#2563eb'}}></div>
                      <span>{t("initialInvestmentLegend")}</span>
                    </div>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: '#8b5cf6'}}></div>
                      <span>{t("contributionsLegend")}</span>
                    </div>
                    <div style={legendItemStyle}>
                      <div style={{...legendColorStyle, background: '#f59e0b'}}></div>
                      <span>{t("interestLegend")}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {schedule.length > 0 && (
            <div style={scheduleTableStyle}>
              <h3 style={h3Style}>{t("scheduleTitle")}</h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thFirstStyle}>{t("yearColumn")}</th>
                    <th style={thStyle}>{t("principalColumn")}</th>
                    <th style={thStyle}>{t("interestColumn")}</th>
                    <th style={thStyle}>{t("balanceColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((row) => (
                    <tr key={row.year}>
                      <td style={tdFirstStyle}>{row.year}</td>
                      <td style={tdStyle}>{formatCurrency(row.principal)}</td>
                      <td style={tdStyle}>{formatCurrency(row.interest)}</td>
                      <td style={tdStyle}>{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step1Title")}</strong> {t("step1Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step2Title")}</strong> {t("step2Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step3Title")}</strong> {t("step3Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step4Title")}</strong> {t("step4Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("step5Title")}</strong> {t("step5Description")}
          </li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("insight1Title")}</strong> {t("insight1Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("insight2Title")}</strong> {t("insight2Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("insight3Title")}</strong> {t("insight3Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("insight4Title")}</strong> {t("insight4Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("insight5Title")}</strong> {t("insight5Description")}
          </li>
        </ul>
      </section>

      {/* Understanding Interest Types */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("simpleInterestTitle")}
        </h3>
        <p style={paragraphStyle}>{t("simpleInterestDescription")}</p>

        <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("compoundInterestTitle")}
        </h3>
        <p style={paragraphStyle}>{t("compoundInterestDescription")}</p>

        <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>
          {t("compoundingFrequencyTitle")}
        </h3>
        <p style={paragraphStyle}>{t("compoundingFrequencyDescription")}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("practice1Title")}</strong> {t("practice1Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("practice2Title")}</strong> {t("practice2Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("practice3Title")}</strong> {t("practice3Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("practice4Title")}</strong> {t("practice4Description")}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t("practice5Title")}</strong> {t("practice5Description")}
          </li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>
            {t("faq1Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>
            {t("faq2Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>
            {t("faq3Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>
            {t("faq4Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div>
          <h3 style={{...h3Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem'}}>
            {t("faq5Question")}
          </h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.aavas.in/blog/benefits-home-loan-interest-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.axismaxlife.com/calculators/compound-interest-calculator" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://elementor.com/tools/interest-calculator/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.stash.com/learn/the-best-compound-interest-calculators-online/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference4")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.investor.gov" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              {t("reference5")}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}